import React, {FC, PropsWithChildren, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {getWidthOfElement} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {renderTagIcon} from '../../../../components/common/dropdowns/dropdownItemTagIcon';
import {useMeasureElement} from '../../../../helpers/hooks/domHookHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {ConversationCardTag} from '../sections/conversations/conversationCardTag';

/*
 * Consts.
 */

const tagsTooltipWidth = 260;
const tagCardRightMargin = 4;

// We set aside this fixed amount of space for the overflow item - we
// need to determine this before computing the tag spacing to avoid a
// circular calculation.
const moreTagsOverflowWidth = 30;

/*
 * Styles.
 */

const StyledExpandTagDiv = styled.div`
  height: 16px;
  ${flexCenter}
  background-color: ${(p) => p.theme.greys.shade70};
  color: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  padding: 2px 4px;
  border-radius: 3px;
  &:hover {
    background-color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledTagsContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTagDiv = styled.div`
  height: 30px;
  padding: 0 8px;
  ${flexCenter}
  overflow-x: hidden;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledTagIconDiv = styled.div`
  margin-right: 12px;
`;

const StyledTagName = styled.div`
  ${ellipsis()}
`;

const StyledConversationCardTagDiv = styled.div<{$isHidden: boolean}>`
  margin-right: ${tagCardRightMargin}px;
  display: ${(p) => (p.$isHidden ? 'none' : 'inline-block')};
`;

const StyledHiddenTagCardsContainer = styled(VisuallyHidden)`
  display: flex;
  flex-direction: row;
  /* This styling is needed to enusre the flex items render to their
  full width in the hidden element so that we can get the correct
  width for display. */
  white-space: nowrap;
  overflow-x: visible;
`;

/*
 * Props.
 */

interface ConversationDetailsHeaderTagsProps {
  tags: ReadonlyArray<TagModel>;
}

/*
 * Component.
 */

export const ConversationDetailsHeaderTags: FC<PropsWithChildren<ConversationDetailsHeaderTagsProps>> = ({
  tags,
}) => {
  const [tagsContainerRef, {width: containerWidth}] = useMeasureElement();
  const hiddenTagCardsWrapperRef = useRef<HTMLDivElement>(null);
  // If the lastVisibleTadIndex is -1 no tags are visible. Defaults to -1 and then updates
  // to the correct value by the useLayoutEffect hook
  const [lastVisibleTagIndex, setLastVisibleTagIndex] = useState<number>(-1);

  // Used for deteriming the numbers of tags that are visible in cards in the header
  // (the names of the rest are displayed in an overflow tooltip)
  useLayoutEffect(() => {
    const rootElement = hiddenTagCardsWrapperRef.current;

    if (!containerWidth || !rootElement) {
      return;
    }

    // The total avaliable width that we can fill with tag cards.
    const avaliableWidth = containerWidth - moreTagsOverflowWidth;
    // The DOM elements for our tag components in the hidden element.
    const children = Array.from(rootElement.children);
    // The widths of each tag card as rendered in the hidden element.
    const childrenWidths = children.map((e) => Math.ceil(getWidthOfElement(e)) + tagCardRightMargin);
    // The last index that should be visible in the actual header to avoid overflowing.
    const lastVisibleIndex = getLastVisibleTagIndex(avaliableWidth, childrenWidths);

    setLastVisibleTagIndex(lastVisibleIndex);
  }, [containerWidth, tags]);

  if (tags.length === 0) {
    return null;
  }

  // Don't display views in the tags header
  const filteredTags = tags.filter((tag) => !tag.isView);

  const tooltipTags = lastVisibleTagIndex > -1 ? filteredTags.slice(lastVisibleTagIndex + 1) : [];

  return (
    <StyledTagsContainerDiv ref={tagsContainerRef}>
      <StyledHiddenTagCardsContainer ref={hiddenTagCardsWrapperRef}>
        {renderHeaderTags(filteredTags)}
      </StyledHiddenTagCardsContainer>
      {renderHeaderTags(filteredTags, lastVisibleTagIndex)}
      {maybeRenderMoreTagsTooltip(tooltipTags)}
    </StyledTagsContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderHeaderTags(tags: ReadonlyArray<TagModel>, lastVisibleTagIndex?: number) {
  return tags.map((tag, index) => (
    <StyledConversationCardTagDiv
      key={tag.id}
      $isHidden={isDefined(lastVisibleTagIndex) ? index > lastVisibleTagIndex : false}
    >
      <ConversationCardTag tag={tag} />
    </StyledConversationCardTagDiv>
  ));
}

function maybeRenderMoreTagsTooltip(tags: ReadonlyArray<TagModel>) {
  if (tags.length === 0) {
    return null;
  }

  return (
    <TooltipCoordinator
      placement="bottom-start"
      tooltipColor="white"
      maxWidth={tagsTooltipWidth}
      minWidth={tagsTooltipWidth}
      render={() =>
        tags.map((tag) => (
          <StyledTagDiv key={tag.id}>
            <StyledTagIconDiv>{renderTagIcon(tag, false)}</StyledTagIconDiv>{' '}
            <StyledTagName>{tag.name}</StyledTagName>
          </StyledTagDiv>
        ))
      }
    >
      <StyledExpandTagDiv>+{tags.length}</StyledExpandTagDiv>
    </TooltipCoordinator>
  );
}

/**
 * Given an available width to will and an array of children widths, returns the index
 * of the last visible child that will fit fully within the avilable width without overflowing.
 *
 * If no children are visible, returns -1. If all children are visible returns the last index
 * the array.
 */
function getLastVisibleTagIndex(availableWidth: number, childrenWidths: ReadonlyArray<number>) {
  const firstHiddenIndex = childrenWidths.findIndex((_child, childIndex) => {
    // Find the width remaining if the children from index 0 to the current childIndex (inclusive)
    // are summed and subtracted from the total available width
    const availableWidthRemaining =
      availableWidth -
      childrenWidths.reduce(
        (currentWidth, childWidth, index) => (index <= childIndex ? currentWidth + childWidth : currentWidth),
        0,
      );

    return availableWidthRemaining < 0;
  });

  // All indexes are visible.
  if (firstHiddenIndex === -1) {
    return childrenWidths.length - 1;
  }

  // The last visible index is the first hidden index - 1.
  return firstHiddenIndex - 1;
}
